'use client';

import { forwardRef, type ComponentProps } from 'react';
import NextLink from 'next/link';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Typography } from '@/primitives';
import type { InbreedingResult } from './types';

const NoPrefetchNextLink = forwardRef<
  HTMLAnchorElement,
  ComponentProps<typeof NextLink>
>(function NoPrefetchNextLink(props, ref) {
  return <NextLink ref={ref} {...props} prefetch={false} />;
});

type MatingInbreedingLineProps = {
  inbreeding: InbreedingResult;
  /** Link to the pedigree + EPA detail view for this mating (right-clickable / new-tab). */
  pedigreeHref: string;
  onInfoClick: () => void;
  /** Narrow pivoted cards: stack lines, no pipe separators, tighter leading. */
  layout?: 'inline' | 'stacked';
};

export default function MatingInbreedingLine({
  inbreeding,
  pedigreeHref,
  onInfoClick,
  layout = 'inline',
}: MatingInbreedingLineProps) {
  const stacked = layout === 'stacked';

  const coefBlock = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <IconButton
        size="small"
        onClick={onInfoClick}
        aria-label="Inbreeding coefficient information"
        sx={{ p: 0.25, color: 'text.secondary' }}
      >
        <InfoOutlinedIcon sx={{ fontSize: stacked ? '0.9375rem' : '1rem' }} />
      </IconButton>
      <Typography
        variant="bodymedium"
        component="span"
        sx={{ lineHeight: stacked ? 1.35 : undefined }}
      >
        Inbreeding Coefficient: <strong>{inbreeding.coefficientPercent}</strong>
      </Typography>
    </Box>
  );

  const genBlock = (
    <Typography
      variant="bodymedium"
      component="span"
      sx={{ lineHeight: stacked ? 1.35 : undefined }}
    >
      Min./(Avg.) generation: {inbreeding.minimumGenerations}/(
      {inbreeding.averageRelatedness.toFixed(1)})
    </Typography>
  );

  const pedigreeLink = (
    <Button
      component={NoPrefetchNextLink}
      href={pedigreeHref}
      variant="outlined"
      size="small"
      sx={{
        textTransform: 'none',
        fontSize: stacked ? '0.75rem' : '0.8125rem',
        py: 0.25,
        px: 1,
        minHeight: 0,
        alignSelf: stacked ? 'flex-start' : undefined,
        mt: stacked ? 0.5 : undefined,
      }}
    >
      View Pedigree and EPA for this mating
    </Button>
  );

  if (stacked) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0.25,
          py: 0,
          px: 0.5,
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        {coefBlock}
        {genBlock}
        {pedigreeLink}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        py: 0,
        px: 1,
        fontSize: '0.8125rem',
        color: 'text.secondary',
      }}
    >
      {coefBlock}

      <Typography variant="bodymedium" component="span" sx={{ mx: 1 }}>
        |
      </Typography>

      {genBlock}

      <Typography variant="bodymedium" component="span" sx={{ mx: 1 }}>
        |
      </Typography>

      {pedigreeLink}
    </Box>
  );
}
